import { useState, useRef, useEffect, useCallback, useMemo } from 'react'
import {
  Box,
  Button,
  ButtonGroup,
  Chip,
  Divider,
  IconButton,
  Tooltip,
  Typography,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  useTheme,
  useMediaQuery,
  CircularProgress,
} from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents'
import SecurityIcon from '@mui/icons-material/Security'
import CancelIcon from '@mui/icons-material/Cancel'
import ExitToAppIcon from '@mui/icons-material/ExitToApp'
import LoopIcon from '@mui/icons-material/Loop'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import SaveIcon from '@mui/icons-material/Save'
import UndoIcon from '@mui/icons-material/Undo'
import RedoIcon from '@mui/icons-material/Redo'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import WarningIcon from '@mui/icons-material/Warning'
import ZoomInIcon from '@mui/icons-material/ZoomIn'
import ZoomOutIcon from '@mui/icons-material/ZoomOut'
import FitScreenIcon from '@mui/icons-material/FitScreen'
import MapIcon from '@mui/icons-material/Map'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import AccountTreeIcon from '@mui/icons-material/AccountTree'
import QuizIcon from '@mui/icons-material/Quiz'
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import DialpadIcon from '@mui/icons-material/Dialpad'
import AbcIcon from '@mui/icons-material/Abc'
import MenuIcon from '@mui/icons-material/Menu'
import MenuBookIcon from '@mui/icons-material/MenuBook'
import HistoryIcon from '@mui/icons-material/History'
import TimelineIcon from '@mui/icons-material/Timeline'
import VisibilityIcon from '@mui/icons-material/Visibility'
import PublishIcon from '@mui/icons-material/Publish'
import DeleteIcon from '@mui/icons-material/Delete'
// Reward icons
import LocationOnIcon from '@mui/icons-material/LocationOn'
import TextSnippetIcon from '@mui/icons-material/TextSnippet'
import HeadphonesIcon from '@mui/icons-material/Headphones'
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary'
import ImageIcon from '@mui/icons-material/Image'
import LockOpenIcon from '@mui/icons-material/LockOpen'
import TimerIcon from '@mui/icons-material/Timer'
import ArticleIcon from '@mui/icons-material/Article'
import GroupIcon from '@mui/icons-material/Group'
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard'
import RouteIcon from '@mui/icons-material/Route'
import PauseCircleIcon from '@mui/icons-material/PauseCircle'
import { useReactFlow } from '@xyflow/react'
import BuildIcon from '@mui/icons-material/Build'
import PlayArrowIcon from '@mui/icons-material/PlayArrow'
import ScienceIcon from '@mui/icons-material/Science'
import { WorkflowNodeType, WorkflowConditionType, getNodeTypeColor, FlowVersionStatus } from '@features/adventures/types/workflow.types'
import { AdventureMainCategory } from '@features/adventures/types/adventure-editor.types'
import { useAdventureEditorStore } from '@features/adventures/stores/adventureEditorStore'
import type { WorkflowValidationResult } from '@features/adventures/types/validation.types'
import { useWorkflowStore } from '@store/workflowStore'
import { type SectionId, ribbonSections } from '../editor/AdventureRibbon'

// Challenge/Gate type definitions - things the user must actively do/pass
// Each challenge now has its own dedicated node type
const challengeTypes = [
  {
    id: 'question',
    label: 'Question/Answer',
    icon: QuizIcon,
    nodeType: WorkflowNodeType.QuestionAnswer,
    description: 'User must answer a question correctly',
  },
  {
    id: 'qr',
    label: 'QR Scanner',
    icon: QrCodeScannerIcon,
    nodeType: WorkflowNodeType.QRCode,
    description: 'User must scan a QR code',
  },
  {
    id: 'numberpad',
    label: 'Number Pad',
    icon: DialpadIcon,
    nodeType: WorkflowNodeType.NumericInput,
    description: 'User must enter a numeric code',
  },
  {
    id: 'codex',
    label: 'Codex',
    icon: AbcIcon,
    nodeType: WorkflowNodeType.CodexInput,
    description: 'User must enter a letter code',
  },
  {
    id: 'approval',
    label: 'Moderator Approval',
    icon: AdminPanelSettingsIcon,
    nodeType: WorkflowNodeType.ModeratorApproval,
    description: 'Requires moderator to approve progress',
  },
]

// Flow control type definitions - how the workflow progresses/branches
const flowControlTypes = [
  {
    id: 'logical',
    label: 'Logical Conditions',
    icon: AccountTreeIcon,
    nodeType: WorkflowNodeType.Decision,
    conditionType: null, // Uses multiple conditions
    description: 'Branch on weather, location, time, etc.',
  },
  {
    id: 'loop',
    label: 'Loop',
    icon: LoopIcon,
    nodeType: WorkflowNodeType.Loop,
    conditionType: null,
    description: 'Repeat a section of the workflow',
  },
  {
    id: 'delay',
    label: 'Delay',
    icon: HourglassEmptyIcon,
    nodeType: WorkflowNodeType.Delay,
    conditionType: null,
    description: 'Pause before continuing',
  },
  {
    id: 'timer_control',
    label: 'Timer Control',
    icon: PauseCircleIcon,
    nodeType: WorkflowNodeType.TimerControl,
    conditionType: null,
    description: 'Pause or resume the adventure clock',
  },
]

// Journal type definitions - content/media displayed in the adventure journal
const journalTypes = [
  {
    id: 'route',
    label: 'Route',
    icon: RouteIcon,
    nodeType: WorkflowNodeType.Route,
    description: 'Display a route between locations',
  },
  {
    id: 'location',
    label: 'Location',
    icon: LocationOnIcon,
    nodeType: WorkflowNodeType.PoiLocation,
    description: 'Reveal a map location to the user',
  },
  {
    id: 'text_clue',
    label: 'Clue Gate',
    icon: TextSnippetIcon,
    nodeType: WorkflowNodeType.TextClue,
    description: 'Unlock clues into the player\'s session pool',
  },
  {
    id: 'audio',
    label: 'Audio Player',
    icon: HeadphonesIcon,
    nodeType: WorkflowNodeType.AudioPlayer,
    description: 'Play an audio file for the user',
  },
  {
    id: 'video',
    label: 'Video Player',
    icon: VideoLibraryIcon,
    nodeType: WorkflowNodeType.VideoPlayer,
    description: 'Play a video for the user',
  },
  {
    id: 'image',
    label: 'Image Display',
    icon: ImageIcon,
    nodeType: WorkflowNodeType.ImageDisplay,
    description: 'Show an image to the user',
  },
  {
    id: 'formatted_message',
    label: 'Formatted Message',
    icon: ArticleIcon,
    nodeType: WorkflowNodeType.FormattedMessage,
    description: 'Rich text document to display',
  },
]

// Reward type definitions for the dropdown menu
// Each reward now has its own dedicated node type
const rewardTypes = [
  {
    id: 'unlock_adventure',
    label: 'Unlock Adventure',
    icon: LockOpenIcon,
    nodeType: WorkflowNodeType.UnlockAdventure,
    description: 'Unlock another adventure for the user',
  },
  {
    id: 'time_modifier',
    label: 'Time Bonus/Penalty',
    icon: TimerIcon,
    nodeType: WorkflowNodeType.TimeModifier,
    description: 'Adjust completion time (bonus or penalty)',
  },
  {
    id: 'group_membership',
    label: 'Group Membership',
    icon: GroupIcon,
    nodeType: WorkflowNodeType.GroupMembership,
    description: 'Add user to a group',
  },
  {
    id: 'gift_reward',
    label: 'Gift Reward',
    icon: CardGiftcardIcon,
    nodeType: WorkflowNodeType.GiftReward,
    description: 'Distribute a gift reward from a template',
  },
]

interface WorkflowToolbarProps {
  onSave: () => void
  onValidate: () => void
  onCompile: () => void
  onStartTesting: () => void
  onPublish: () => void
  isSaving: boolean
  isCompiling?: boolean
  isDirty: boolean
  versionNumber: number | null
  versionStatus: FlowVersionStatus | null
  isCompiled: boolean
  isOwner: boolean  // Only owner can publish
  isReadOnly?: boolean  // When true, disable all editing actions (published adventure)
  showMiniMap: boolean
  onToggleMiniMap: () => void
  // Undo/Redo
  onUndo: () => void
  onRedo: () => void
  canUndo: boolean
  canRedo: boolean
  // Adventure type for filtering available nodes
  mainCategory?: AdventureMainCategory
  // Category-specific allowed nodes (null = all allowed)
  allowedNodes?: number[] | null
  // Editor ribbon props for mobile hamburger menu
  editorMenuProps?: {
    activeSection: SectionId
    onSectionChanged: (section: SectionId) => void
    onEditorSave?: () => void
    onPublish?: () => void
    onDelete?: () => void
    onHistory?: () => void
    onFlowVersionHistory?: () => void
    onImportFlow?: () => void
    isEditorSaving?: boolean
    isPublishing?: boolean
    isReadyToPublish?: boolean
    hiddenSections?: SectionId[]
    workflowAllowed?: boolean
  }
}

export function WorkflowToolbar({
  onSave,
  onValidate,
  onCompile,
  onStartTesting,
  onPublish,
  isSaving,
  isCompiling,
  isDirty,
  versionNumber,
  versionStatus,
  isCompiled,
  isOwner,
  isReadOnly = false,
  showMiniMap,
  onToggleMiniMap,
  onUndo,
  onRedo,
  canUndo,
  canRedo,
  mainCategory,
  allowedNodes,
  editorMenuProps,
}: WorkflowToolbarProps) {
  const theme = useTheme()
  const isMobile = useMediaQuery(theme.breakpoints.down('md'))
  const { zoomIn, zoomOut, fitView, getViewport } = useReactFlow()
  const { addNode: addNodeLocal, nodes, validationResult } = useWorkflowStore()
  const [challengesMenuAnchor, setChallengesMenuAnchor] = useState<null | HTMLElement>(null)
  const [rewardsMenuAnchor, setRewardsMenuAnchor] = useState<null | HTMLElement>(null)
  const [flowControlMenuAnchor, setFlowControlMenuAnchor] = useState<null | HTMLElement>(null)
  const [journalMenuAnchor, setJournalMenuAnchor] = useState<null | HTMLElement>(null)
  const [exitMenuAnchor, setExitMenuAnchor] = useState<null | HTMLElement>(null)
  const [editorMenuAnchor, setEditorMenuAnchor] = useState<null | HTMLElement>(null)
  const [scrollIndicator, setScrollIndicator] = useState<'left' | 'right' | 'both' | null>(null)

  // Surface the testing workflow version independently of the currently-loaded flow.
  // A user can be editing a newer Draft (v13) while an older compiled version (v12) is
  // actively in Testing — the toolbar's Compile/Start Testing buttons act on the loaded
  // version, so we show a separate chip so the tester knows a different version is live.
  const adventureHasTestVersion = useAdventureEditorStore((s) => s.adventure?.hasTestVersion)
  const testingWorkflowVersion = useAdventureEditorStore((s) => s.adventure?.testingWorkflowVersion)
  const publishedWorkflowVersion = useAdventureEditorStore((s) => s.adventure?.publishedWorkflowVersion)
  const showTestingBadge = adventureHasTestVersion === true &&
    testingWorkflowVersion != null &&
    versionNumber !== testingWorkflowVersion  // hide when we're editing the test version itself
  const scrollContainerRef = useRef<HTMLDivElement>(null)

  // Validation button state helper
  const getValidationButtonState = () => {
    if (!validationResult) {
      return { color: 'inherit' as const, icon: <CheckCircleIcon fontSize="small" />, tooltip: 'Validate Workflow' }
    }
    if (validationResult.errorCount > 0) {
      const count = validationResult.errorCount
      return {
        color: 'error' as const,
        icon: (
          <Box
            sx={{
              minWidth: 20,
              minHeight: 20,
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: 'error.main',
              color: 'white',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            {count > 99 ? '99+' : count}
          </Box>
        ),
        tooltip: `${count} error${count !== 1 ? 's' : ''} found`,
      }
    }
    if (validationResult.warningCount > 0) {
      const count = validationResult.warningCount
      return {
        color: 'warning' as const,
        icon: (
          <Box
            sx={{
              minWidth: 20,
              minHeight: 20,
              width: 20,
              height: 20,
              borderRadius: '50%',
              backgroundColor: 'warning.main',
              color: 'white',
              fontSize: '0.9rem',
              fontWeight: 'bold',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              lineHeight: 1,
              flexShrink: 0,
            }}
          >
            {count > 99 ? '99+' : count}
          </Box>
        ),
        tooltip: `${count} warning${count !== 1 ? 's' : ''} found`,
      }
    }
    return { color: 'success' as const, icon: <CheckCircleIcon fontSize="small" />, tooltip: 'Validation passed' }
  }

  const validationButtonState = getValidationButtonState()

  // Helper to filter node types by allowed nodes list
  const filterNodeTypes = useCallback(<T extends { nodeType: WorkflowNodeType }>(
    nodeTypes: T[],
    allowedNodesList: number[] | null | undefined
  ): T[] => {
    // null or empty = all allowed
    if (!allowedNodesList || allowedNodesList.length === 0) {
      return nodeTypes
    }
    return nodeTypes.filter(n => allowedNodesList.includes(n.nodeType))
  }, [])

  // Filter challenge types for Trail adventures (hide all challenge types) and by category allowedNodes
  const isTrailAdventure = mainCategory === AdventureMainCategory.Trails
  const filteredChallengeTypes = useMemo(() => {
    if (isTrailAdventure) return []
    return filterNodeTypes(challengeTypes, allowedNodes)
  }, [isTrailAdventure, allowedNodes, filterNodeTypes])

  // Filter flow control types by category allowedNodes
  const filteredFlowControlTypes = useMemo(() => {
    return filterNodeTypes(flowControlTypes, allowedNodes)
  }, [allowedNodes, filterNodeTypes])

  // Filter journal types by category allowedNodes
  const filteredJournalTypes = useMemo(() => {
    return filterNodeTypes(journalTypes, allowedNodes)
  }, [allowedNodes, filterNodeTypes])

  // Filter reward types by category allowedNodes
  const filteredRewardTypes = useMemo(() => {
    return filterNodeTypes(rewardTypes, allowedNodes)
  }, [allowedNodes, filterNodeTypes])

  // Set up scroll listener and check on mount/resize
  useEffect(() => {
    const container = scrollContainerRef.current
    if (!container) return

    const updateScrollIndicator = () => {
      if (!isMobile) {
        setScrollIndicator(null)
        return
      }

      const hasMoreContent = container.scrollWidth > container.clientWidth
      if (!hasMoreContent) {
        setScrollIndicator(null)
        return
      }

      const scrollLeft = container.scrollLeft
      const maxScroll = container.scrollWidth - container.clientWidth
      const isAtStart = scrollLeft <= 10
      const isAtEnd = scrollLeft >= maxScroll - 10

      if (isAtStart && !isAtEnd) {
        setScrollIndicator('right')
      } else if (isAtEnd && !isAtStart) {
        setScrollIndicator('left')
      } else if (!isAtStart && !isAtEnd) {
        setScrollIndicator('both')
      } else {
        setScrollIndicator(null)
      }
    }

    // Initial check
    updateScrollIndicator()

    // Listen to scroll events
    container.addEventListener('scroll', updateScrollIndicator, { passive: true })
    window.addEventListener('resize', updateScrollIndicator)

    return () => {
      container.removeEventListener('scroll', updateScrollIndicator)
      window.removeEventListener('resize', updateScrollIndicator)
    }
  }, [isMobile])

  const handleEditorMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setEditorMenuAnchor(event.currentTarget)
  }

  const handleEditorMenuClose = () => {
    setEditorMenuAnchor(null)
  }

  const handleEditorMenuItemClick = (sectionId: SectionId) => {
    if (editorMenuProps?.onSectionChanged) {
      editorMenuProps.onSectionChanged(sectionId)
    }
    handleEditorMenuClose()
  }

  const handleEditorAction = (action: 'save' | 'publish' | 'delete' | 'history' | 'flowhistory' | 'flowimport') => {
    if (action === 'save' && editorMenuProps?.onEditorSave) {
      editorMenuProps.onEditorSave()
    } else if (action === 'publish' && editorMenuProps?.onPublish) {
      editorMenuProps.onPublish()
    } else if (action === 'delete' && editorMenuProps?.onDelete) {
      editorMenuProps.onDelete()
    } else if (action === 'history' && editorMenuProps?.onHistory) {
      editorMenuProps.onHistory()
    } else if (action === 'flowhistory' && editorMenuProps?.onFlowVersionHistory) {
      editorMenuProps.onFlowVersionHistory()
    } else if (action === 'flowimport' && editorMenuProps?.onImportFlow) {
      editorMenuProps.onImportFlow()
    }
    handleEditorMenuClose()
  }

  const handleAddNode = (type: WorkflowNodeType, label?: string, conditionType?: WorkflowConditionType | null, rewardType?: string) => {
    // Block adding nodes when read-only
    if (isReadOnly) return

    const viewport = getViewport()
    // Place new node in the center of the current viewport
    const position = {
      x: (-viewport.x + 400) / viewport.zoom,
      y: (-viewport.y + 300) / viewport.zoom,
    }
    // Add node locally (no real-time sync)
    addNodeLocal(type, position, label, conditionType ?? undefined, rewardType)
  }

  const handleChallengesMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setChallengesMenuAnchor(event.currentTarget)
  }

  const handleChallengesMenuClose = () => {
    setChallengesMenuAnchor(null)
  }

  const handleAddChallenge = (challengeType: typeof challengeTypes[number]) => {
    // Challenge nodes now have their own dedicated node types
    handleAddNode(challengeType.nodeType, challengeType.label)
    handleChallengesMenuClose()
  }

  const handleRewardsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setRewardsMenuAnchor(event.currentTarget)
  }

  const handleRewardsMenuClose = () => {
    setRewardsMenuAnchor(null)
  }

  const handleAddReward = (rewType: typeof rewardTypes[number]) => {
    // Reward nodes now have their own dedicated node types
    handleAddNode(rewType.nodeType, rewType.label)
    handleRewardsMenuClose()
  }

  const handleFlowControlMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setFlowControlMenuAnchor(event.currentTarget)
  }

  const handleFlowControlMenuClose = () => {
    setFlowControlMenuAnchor(null)
  }

  const handleAddFlowControl = (flowType: typeof flowControlTypes[number]) => {
    handleAddNode(flowType.nodeType, flowType.label, flowType.conditionType)
    handleFlowControlMenuClose()
  }

  const handleJournalMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setJournalMenuAnchor(event.currentTarget)
  }

  const handleJournalMenuClose = () => {
    setJournalMenuAnchor(null)
  }

  const handleAddJournal = (journalType: typeof journalTypes[number]) => {
    handleAddNode(journalType.nodeType, journalType.label)
    handleJournalMenuClose()
  }

  const handleExitMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setExitMenuAnchor(event.currentTarget)
  }

  const handleExitMenuClose = () => {
    setExitMenuAnchor(null)
  }

  const handleAddExit = (exitType: WorkflowNodeType) => {
    handleAddNode(exitType)
    handleExitMenuClose()
  }

  return (
    <Box
      sx={{
        position: 'relative',
        backgroundColor: 'background.paper',
        borderBottom: 1,
        borderColor: 'divider',
      }}
    >
      {/* Read-only banner for published adventures */}
      {isReadOnly && (
        <Box
          sx={{
            px: 2,
            py: 0.75,
            backgroundColor: 'warning.light',
            color: 'warning.contrastText',
            fontSize: '0.875rem',
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: 1,
            borderColor: 'warning.main',
          }}
        >
          <VisibilityIcon fontSize="small" />
          <Typography variant="body2" fontWeight={500}>
            Read-only mode — This adventure is published. You can view the workflow and export JSON, but editing is disabled.
          </Typography>
        </Box>
      )}

      {/* Scroll indicator - Left */}
      {(scrollIndicator === 'left' || scrollIndicator === 'both') && (
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            top: 0,
            bottom: 0,
            width: 28,
            background: 'linear-gradient(to left, transparent, rgba(255,255,255,0.95))',
            pointerEvents: 'none',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            pl: 0.5,
          }}
        >
          <ChevronLeftIcon
            fontSize="small"
            sx={{
              color: 'action.active',
              animation: 'pulseLeft 1.5s ease-in-out infinite',
              '@keyframes pulseLeft': {
                '0%, 100%': { opacity: 0.5, transform: 'translateX(0)' },
                '50%': { opacity: 1, transform: 'translateX(-3px)' },
              },
            }}
          />
        </Box>
      )}

      {/* Scroll indicator - Right */}
      {(scrollIndicator === 'right' || scrollIndicator === 'both') && (
        <Box
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 28,
            background: 'linear-gradient(to right, transparent, rgba(255,255,255,0.95))',
            pointerEvents: 'none',
            zIndex: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            pr: 0.5,
          }}
        >
          <ChevronRightIcon
            fontSize="small"
            sx={{
              color: 'action.active',
              animation: 'pulseRight 1.5s ease-in-out infinite',
              '@keyframes pulseRight': {
                '0%, 100%': { opacity: 0.5, transform: 'translateX(0)' },
                '50%': { opacity: 1, transform: 'translateX(3px)' },
              },
            }}
          />
        </Box>
      )}

      {/* Scrollable toolbar content */}
      <Box
        ref={scrollContainerRef}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: { xs: 0.5, sm: 1 },
          p: { xs: 0.5, sm: 1 },
          flexWrap: 'nowrap',
          overflowX: 'auto',
          // Hide scrollbar but keep functionality
          scrollbarWidth: 'none',
          '&::-webkit-scrollbar': { display: 'none' },
        }}
      >
      {/* Editor Menu Hamburger (mobile only) */}
      {isMobile && editorMenuProps && (
        <>
          <Tooltip title="Editor Menu">
            <IconButton size="small" onClick={handleEditorMenuOpen}>
              <MenuIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={editorMenuAnchor}
            open={Boolean(editorMenuAnchor)}
            onClose={handleEditorMenuClose}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'left',
            }}
            transformOrigin={{
              vertical: 'top',
              horizontal: 'left',
            }}
            slotProps={{
              paper: {
                sx: { maxHeight: '70vh', minWidth: 200 }
              }
            }}
          >
            {ribbonSections
              .filter((section) => section.header !== 'Actions') // Actions section handled separately below
              .map((section, sectionIndex) => {
                const hiddenSet = editorMenuProps.hiddenSections ?? []
                const workflowAllowed = editorMenuProps.workflowAllowed ?? true
                const visibleItems = section.items.filter((item) => {
                  // Hide nav items that are in hiddenSections
                  if (!item.isAction && hiddenSet.includes(item.id as SectionId)) return false
                  // Hide flow actions when workflow is not allowed
                  if ((item.id === 'flowhistory' || item.id === 'flowimport') && !workflowAllowed) return false
                  return true
                })
                if (visibleItems.length === 0) return null
                return [
                  sectionIndex > 0 && <Divider key={`divider-${section.header}`} />,
                  <MenuItem key={`header-${section.header}`} disabled sx={{ opacity: '1 !important', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>
                    {section.header}
                  </MenuItem>,
                  ...visibleItems.map((item) => (
                    <MenuItem
                      key={item.id}
                      onClick={() => item.isAction
                        ? handleEditorAction(item.id as 'save' | 'publish' | 'delete' | 'history' | 'flowhistory' | 'flowimport')
                        : handleEditorMenuItemClick(item.id as SectionId)
                      }
                      selected={!item.isAction && editorMenuProps.activeSection === item.id}
                      sx={{
                        pl: 3,
                        ...(item.color === 'error' && { color: 'error.main' }),
                      }}
                    >
                      <ListItemIcon sx={item.color === 'error' ? { color: 'error.main' } : undefined}>
                        {item.icon}
                      </ListItemIcon>
                      <ListItemText primary={item.label} />
                    </MenuItem>
                  )),
                ]
              })}
            <Divider />
            <MenuItem key="header-actions" disabled sx={{ opacity: '1 !important', fontWeight: 600, fontSize: '0.75rem', textTransform: 'uppercase', color: 'text.secondary' }}>
              Actions
            </MenuItem>
            <MenuItem
              onClick={() => handleEditorAction('save')}
              disabled={editorMenuProps.isEditorSaving || editorMenuProps.isPublishing}
              sx={{ pl: 3 }}
            >
              <ListItemIcon>
                {editorMenuProps.isEditorSaving ? <CircularProgress size={20} /> : <SaveIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText primary={editorMenuProps.isEditorSaving ? 'Saving...' : 'Save Adventure'} />
            </MenuItem>
            <MenuItem
              onClick={() => handleEditorAction('publish')}
              disabled={!editorMenuProps.isReadyToPublish || editorMenuProps.isEditorSaving || editorMenuProps.isPublishing}
              sx={{ pl: 3 }}
            >
              <ListItemIcon>
                {editorMenuProps.isPublishing ? <CircularProgress size={20} /> : <PublishIcon fontSize="small" />}
              </ListItemIcon>
              <ListItemText primary={editorMenuProps.isPublishing ? 'Publishing...' : 'Publish'} />
            </MenuItem>
            {editorMenuProps.onHistory && (
              <MenuItem
                onClick={() => handleEditorAction('history')}
                sx={{ pl: 3 }}
              >
                <ListItemIcon>
                  <HistoryIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="History" />
              </MenuItem>
            )}
            {editorMenuProps.onDelete && (
              <MenuItem
                onClick={() => handleEditorAction('delete')}
                sx={{ pl: 3, color: 'error.main' }}
              >
                <ListItemIcon sx={{ color: 'error.main' }}>
                  <DeleteIcon fontSize="small" />
                </ListItemIcon>
                <ListItemText primary="Delete" />
              </MenuItem>
            )}
          </Menu>
          <Divider orientation="vertical" flexItem />
        </>
      )}

      {/* Zoom Controls and Undo/Redo - positioned first for mobile visibility */}
      <Box sx={{ display: 'flex', flexShrink: 0 }}>
        <Tooltip title="Zoom In">
          <IconButton size="small" onClick={() => zoomIn()}>
            <ZoomInIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Zoom Out">
          <IconButton size="small" onClick={() => zoomOut()}>
            <ZoomOutIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Fit View">
          <IconButton size="small" onClick={() => fitView({ padding: 0.2 })}>
            <FitScreenIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title={showMiniMap ? 'Hide Mini Map' : 'Show Mini Map'}>
          <IconButton
            size="small"
            onClick={onToggleMiniMap}
            color={showMiniMap ? 'primary' : 'default'}
          >
            <MapIcon />
          </IconButton>
        </Tooltip>
        {/* Undo/Redo Controls - hidden in read-only mode */}
        {!isReadOnly && (
          <>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <Tooltip title="Undo">
              <span>
                <IconButton size="small" onClick={onUndo} disabled={!canUndo}>
                  <UndoIcon />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Redo">
              <span>
                <IconButton size="small" onClick={onRedo} disabled={!canRedo}>
                  <RedoIcon />
                </IconButton>
              </span>
            </Tooltip>
          </>
        )}
      </Box>

      <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />

      {/* Node Type Buttons */}
      {/* Add Node buttons - hidden in read-only mode */}
      {!isReadOnly && (
        <>
          <Box sx={{ display: 'flex' }}>
            {/* Challenge/Gate button - hidden for Trail adventures or if no challenge types allowed */}
            {filteredChallengeTypes.length > 0 && (
              <Tooltip title="Challenge">
                <IconButton
                  size="small"
                  onClick={handleChallengesMenuOpen}
                  sx={{
                    color: getNodeTypeColor(WorkflowNodeType.QuestionAnswer),
                    '&:hover': { bgcolor: `${getNodeTypeColor(WorkflowNodeType.QuestionAnswer)}20` }
                  }}
                >
                  <SecurityIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {/* Flow Control button - hidden if no flow control types allowed */}
            {filteredFlowControlTypes.length > 0 && (
              <Tooltip title="Flow Control">
                <IconButton
                  size="small"
                  onClick={handleFlowControlMenuOpen}
                  sx={{
                    color: getNodeTypeColor(WorkflowNodeType.Loop),
                    '&:hover': { bgcolor: `${getNodeTypeColor(WorkflowNodeType.Loop)}20` }
                  }}
                >
                  <TimelineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {/* Journal button - hidden if no journal types allowed */}
            {filteredJournalTypes.length > 0 && (
              <Tooltip title="Journal">
                <IconButton
                  size="small"
                  onClick={handleJournalMenuOpen}
                  sx={{
                    color: getNodeTypeColor(WorkflowNodeType.TextClue),
                    '&:hover': { bgcolor: `${getNodeTypeColor(WorkflowNodeType.TextClue)}20` }
                  }}
                >
                  <MenuBookIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {/* Reward button - hidden if no reward types allowed */}
            {filteredRewardTypes.length > 0 && (
              <Tooltip title="Reward">
                <IconButton
                  size="small"
                  onClick={handleRewardsMenuOpen}
                  sx={{
                    color: getNodeTypeColor(WorkflowNodeType.UnlockAdventure),
                    '&:hover': { bgcolor: `${getNodeTypeColor(WorkflowNodeType.UnlockAdventure)}20` }
                  }}
                >
                  <EmojiEventsIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {/* Exit button - always shown (exit nodes are always allowed) */}
            <Tooltip title="Exit">
              <IconButton
                size="small"
                onClick={handleExitMenuOpen}
                sx={{
                  color: getNodeTypeColor(WorkflowNodeType.Exit),
                  '&:hover': { bgcolor: `${getNodeTypeColor(WorkflowNodeType.Exit)}20` }
                }}
              >
                <ExitToAppIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </>
      )}

      {/* Challenges/Gates Type Menu */}
      <Menu
        anchorEl={challengesMenuAnchor}
        open={Boolean(challengesMenuAnchor)}
        onClose={handleChallengesMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {filteredChallengeTypes.map((challengeType) => {
          const IconComponent = challengeType.icon
          return (
            <MenuItem
              key={challengeType.id}
              onClick={() => handleAddChallenge(challengeType)}
              sx={{ minWidth: 200 }}
            >
              <ListItemIcon>
                <IconComponent fontSize="small" sx={{ color: getNodeTypeColor(challengeType.nodeType) }} />
              </ListItemIcon>
              <ListItemText
                primary={challengeType.label}
                secondary={challengeType.description}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </MenuItem>
          )
        })}
      </Menu>

      {/* Rewards Type Menu */}
      <Menu
        anchorEl={rewardsMenuAnchor}
        open={Boolean(rewardsMenuAnchor)}
        onClose={handleRewardsMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {filteredRewardTypes.map((rewType) => {
          const IconComponent = rewType.icon
          return (
            <MenuItem
              key={rewType.id}
              onClick={() => handleAddReward(rewType)}
              sx={{ minWidth: 220 }}
            >
              <ListItemIcon>
                <IconComponent fontSize="small" sx={{ color: getNodeTypeColor(rewType.nodeType) }} />
              </ListItemIcon>
              <ListItemText
                primary={rewType.label}
                secondary={rewType.description}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </MenuItem>
          )
        })}
      </Menu>

      {/* Flow Control Type Menu */}
      <Menu
        anchorEl={flowControlMenuAnchor}
        open={Boolean(flowControlMenuAnchor)}
        onClose={handleFlowControlMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {filteredFlowControlTypes.map((flowType) => {
          const IconComponent = flowType.icon
          return (
            <MenuItem
              key={flowType.id}
              onClick={() => handleAddFlowControl(flowType)}
              sx={{ minWidth: 200 }}
            >
              <ListItemIcon>
                <IconComponent fontSize="small" sx={{ color: getNodeTypeColor(WorkflowNodeType.Loop) }} />
              </ListItemIcon>
              <ListItemText
                primary={flowType.label}
                secondary={flowType.description}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </MenuItem>
          )
        })}
      </Menu>

      {/* Journal Type Menu */}
      <Menu
        anchorEl={journalMenuAnchor}
        open={Boolean(journalMenuAnchor)}
        onClose={handleJournalMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        {filteredJournalTypes.map((journalType) => {
          const IconComponent = journalType.icon
          return (
            <MenuItem
              key={journalType.id}
              onClick={() => handleAddJournal(journalType)}
              sx={{ minWidth: 220 }}
            >
              <ListItemIcon>
                <IconComponent fontSize="small" sx={{ color: getNodeTypeColor(journalType.nodeType) }} />
              </ListItemIcon>
              <ListItemText
                primary={journalType.label}
                secondary={journalType.description}
                secondaryTypographyProps={{ variant: 'caption' }}
              />
            </MenuItem>
          )
        })}
      </Menu>

      {/* Exit Nodes Menu */}
      <Menu
        anchorEl={exitMenuAnchor}
        open={Boolean(exitMenuAnchor)}
        onClose={handleExitMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <MenuItem onClick={() => handleAddExit(WorkflowNodeType.ExitSuccess)} sx={{ minWidth: 180 }}>
          <ListItemIcon>
            <CheckCircleIcon fontSize="small" sx={{ color: getNodeTypeColor(WorkflowNodeType.ExitSuccess) }} />
          </ListItemIcon>
          <ListItemText primary="Success" secondary="Adventure completed successfully" secondaryTypographyProps={{ variant: 'caption' }} />
        </MenuItem>
        <MenuItem onClick={() => handleAddExit(WorkflowNodeType.ExitFailed)}>
          <ListItemIcon>
            <CancelIcon fontSize="small" sx={{ color: getNodeTypeColor(WorkflowNodeType.ExitFailed) }} />
          </ListItemIcon>
          <ListItemText primary="Failed" secondary="Adventure failed" secondaryTypographyProps={{ variant: 'caption' }} />
        </MenuItem>
        <MenuItem onClick={() => handleAddExit(WorkflowNodeType.ExitAbandoned)}>
          <ListItemIcon>
            <ExitToAppIcon fontSize="small" sx={{ color: getNodeTypeColor(WorkflowNodeType.ExitAbandoned) }} />
          </ListItemIcon>
          <ListItemText primary="Abandoned" secondary="User quit/left" secondaryTypographyProps={{ variant: 'caption' }} />
        </MenuItem>
      </Menu>

      {/* Spacer */}
      <Box sx={{ flexGrow: 1 }} />

      {/* Action Buttons - compact on mobile, hidden in read-only mode */}
      {!isReadOnly && isMobile ? (
        <ButtonGroup size="small" variant="outlined">
          <Tooltip title={validationButtonState.tooltip}>
            <Button
              onClick={onValidate}
              color={validationButtonState.color}
              sx={{ minWidth: 'auto', px: 1 }}
            >
              {validationButtonState.icon}
            </Button>
          </Tooltip>
          <Tooltip title={isSaving ? 'Saving...' : 'Save Flow'}>
            <span>
              <Button
                onClick={onSave}
                disabled={isSaving}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                {isSaving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon fontSize="small" />}
              </Button>
            </span>
          </Tooltip>
          <Tooltip title={isCompiling ? 'Compiling...' : 'Compile Flow'}>
            <span>
              <Button
                onClick={onCompile}
                disabled={isDirty || isCompiling}
                sx={{ minWidth: 'auto', px: 1 }}
              >
                {isCompiling ? <CircularProgress size={18} color="inherit" /> : <BuildIcon fontSize="small" />}
              </Button>
            </span>
          </Tooltip>
          {showTestingBadge && (
            <Tooltip title={`Workflow v${testingWorkflowVersion} is currently in Testing${publishedWorkflowVersion ? ` (published v${publishedWorkflowVersion})` : ''}. You're editing a newer draft.`}>
              <Chip
                icon={<ScienceIcon sx={{ fontSize: 14 }} />}
                label={`Testing v${testingWorkflowVersion}`}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ alignSelf: 'center', fontWeight: 600, ml: 0.5 }}
              />
            </Tooltip>
          )}
          {versionStatus === FlowVersionStatus.Draft && isCompiled && (
            <Tooltip title="Start Testing">
              <Button onClick={onStartTesting} sx={{ minWidth: 'auto', px: 1 }}>
                <PlayArrowIcon fontSize="small" />
              </Button>
            </Tooltip>
          )}
          {versionStatus === FlowVersionStatus.Testing && isOwner && (
            <Tooltip title="Publish">
              <Button onClick={onPublish} variant="contained" color="success" sx={{ minWidth: 'auto', px: 1 }}>
                <PublishIcon fontSize="small" />
              </Button>
            </Tooltip>
          )}
          {versionStatus === FlowVersionStatus.Testing && !isOwner && (
            <Tooltip title="Only the adventure owner can publish">
              <span>
                <Button disabled variant="outlined" sx={{ minWidth: 'auto', px: 1 }}>
                  <PublishIcon fontSize="small" />
                </Button>
              </span>
            </Tooltip>
          )}
        </ButtonGroup>
      ) : !isReadOnly ? (
        <Box sx={{ display: 'flex' }}>
          <Tooltip title={validationButtonState.tooltip}>
            <IconButton
              size="small"
              color={validationButtonState.color}
              onClick={onValidate}
            >
              {validationButtonState.icon}
            </IconButton>
          </Tooltip>
          <Tooltip title={isSaving ? 'Saving...' : 'Save'}>
            <span>
              <IconButton
                size="small"
                onClick={onSave}
                disabled={isSaving}
                sx={{ color: 'primary.main' }}
              >
                {isSaving ? <CircularProgress size={18} /> : <SaveIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title={isCompiling ? 'Compiling...' : isDirty ? 'Save first to compile' : 'Compile'}>
            <span>
              <IconButton
                size="small"
                onClick={onCompile}
                disabled={isDirty || isCompiling}
                sx={{ color: 'secondary.main' }}
              >
                {isCompiling ? <CircularProgress size={18} /> : <BuildIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
          {showTestingBadge && (
            <Tooltip title={`Workflow v${testingWorkflowVersion} is currently in Testing${publishedWorkflowVersion ? ` (published v${publishedWorkflowVersion})` : ''}. You're editing a newer draft.`}>
              <Chip
                icon={<ScienceIcon sx={{ fontSize: 14 }} />}
                label={`Testing v${testingWorkflowVersion}`}
                size="small"
                color="warning"
                variant="outlined"
                sx={{ alignSelf: 'center', fontWeight: 600, mx: 0.5 }}
              />
            </Tooltip>
          )}
          {versionStatus === FlowVersionStatus.Draft && isCompiled && (
            <Tooltip title="Start Testing">
              <IconButton
                size="small"
                onClick={onStartTesting}
                sx={{ color: 'info.main' }}
              >
                <PlayArrowIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {versionStatus === FlowVersionStatus.Testing && isOwner && (
            <Tooltip title="Publish">
              <IconButton
                size="small"
                onClick={onPublish}
                sx={{
                  color: 'success.main',
                  bgcolor: 'success.light',
                  '&:hover': { bgcolor: 'success.main', color: 'white' }
                }}
              >
                <PublishIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {versionStatus === FlowVersionStatus.Testing && !isOwner && (
            <Tooltip title="Only the adventure owner can publish">
              <span>
                <IconButton size="small" disabled>
                  <PublishIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Box>
      ) : null}

      </Box>{/* End of scrollable toolbar content */}
    </Box>
  )
}
